import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaAt,
  FaBold,
  FaCalendarAlt,
  FaExternalLinkAlt,
  FaGlobe,
  FaItalic,
  FaLink,
  FaListOl,
  FaListUl,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRegStar,
  FaSave,
  FaStar,
  FaTimes,
  FaTrash,
  FaUnderline,
  FaUserTie,
} from "react-icons/fa";

import { formatDateFR, getLogoUrl } from "../utils/suiviClientsUtils";

const EMPTY_FORM = {
  nom_asso: "",
  produit_asso: "",
  statut_asso: "",
  date_relance_asso: "",
  last_contact_asso: "",
  adresse_asso: "",
  tel_asso: "",
  mail_asso: "",
  siret_asso: "",
  discipline_asso: "",
  siteweb_asso: "",
  apiUrl_asso: "",
  detail_asso: "",
  logo_asso: "",
  is_favorite: false,
};

function toDateInput(value) {
  if (!value) return "";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
}

function sanitizeHtml(html = "") {
  return String(html || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/\sjavascript:/gi, "");
}

export default function SuiviClientDetailModal({
  association,
  columns = [],
  productOptions = [],
  loading,
  saving,
  onClose,
  onSave,
  onAskDelete,
}) {
  const dialogRef = useRef(null);

  const [form, setForm] = useState(EMPTY_FORM);

  const logoUrl = getLogoUrl(form.logo_asso);

  const currentColumn = useMemo(() => {
    return columns.find((column) => column.status === form.statut_asso);
  }, [columns, form.statut_asso]);

  const contacts = useMemo(() => {
    if (Array.isArray(association?.prospects)) {
      return association.prospects;
    }

    if (Array.isArray(association?.Prospects)) {
      return association.Prospects;
    }

    return [];
  }, [association]);

  const productChoices = useMemo(() => {
    const values = [];

    [...(productOptions || []), form.produit_asso].forEach((value) => {
      const cleanValue = String(value || "").trim();

      if (cleanValue && !values.includes(cleanValue)) {
        values.push(cleanValue);
      }
    });

    return values.sort((a, b) => String(a).localeCompare(String(b), "fr"));
  }, [productOptions, form.produit_asso]);

  useEffect(() => {
    if (!association) return;

    setForm({
      ...EMPTY_FORM,
      ...association,
      date_relance_asso: toDateInput(association.date_relance_asso),
      last_contact_asso: toDateInput(association.last_contact_asso),
      is_favorite: Boolean(association.is_favorite),
      detail_asso: association.detail_asso || "",
    });
  }, [association]);

  useEffect(() => {
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const timer = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='nom_asso']")?.focus();
    }, 80);

    const onEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEsc);

    return () => {
      root.style.overflow = previousOverflow;
      clearTimeout(timer);
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const updateField = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!association?.id) return;

    const patch = {
      nom_asso: form.nom_asso || "",
      produit_asso: form.produit_asso || "",
      statut_asso: form.statut_asso || "",
      date_relance_asso: form.date_relance_asso || null,
      last_contact_asso: form.last_contact_asso || null,
      adresse_asso: form.adresse_asso || "",
      tel_asso: form.tel_asso || "",
      mail_asso: form.mail_asso || "",
      siret_asso: form.siret_asso || "",
      discipline_asso: form.discipline_asso || "",
      siteweb_asso: form.siteweb_asso || "",
      apiUrl_asso: form.apiUrl_asso || "",
      detail_asso: sanitizeHtml(form.detail_asso || ""),
      logo_asso: form.logo_asso || "",
      is_favorite: Boolean(form.is_favorite),
    };

    onSave?.(association.id, patch);
  };

  if (!association) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] overflow-y-auto bg-slate-950/75 px-2 py-4 backdrop-blur-sm sm:px-6"
      role="dialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className="mx-auto my-2 max-w-6xl overflow-hidden rounded-2xl bg-[#f1f2f4] shadow-2xl ring-1 ring-white/20"
      >
        <div className="relative min-h-32 bg-slate-200">
          {logoUrl ? (
            <div className="flex h-44 items-center justify-center bg-slate-200">
              <img
                src={logoUrl}
                alt={form.nom_asso || "Logo association"}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : (
            <div
              className="h-28"
              style={{ backgroundColor: currentColumn?.color || "#c4b5fd" }}
            />
          )}

          <div className="absolute left-4 top-4">
            <select
              value={form.statut_asso || ""}
              onChange={(event) =>
                updateField("statut_asso", event.target.value)
              }
              className="rounded-md border border-white/40 bg-white/90 px-3 py-1.5 text-sm font-bold text-slate-700 shadow-sm outline-none"
            >
              {columns.map((column) => (
                <option key={column.id} value={column.status}>
                  {column.title}
                </option>
              ))}
            </select>
          </div>

          <div className="absolute right-3 top-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => updateField("is_favorite", !form.is_favorite)}
              className={`grid h-10 w-10 place-items-center rounded-full shadow-sm transition ${
                form.is_favorite
                  ? "bg-yellow-100 text-yellow-500"
                  : "bg-white text-slate-500 hover:bg-slate-50"
              }`}
              title="Favori"
            >
              {form.is_favorite ? <FaStar /> : <FaRegStar />}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="grid h-10 w-10 place-items-center rounded-full bg-white text-slate-600 shadow-sm transition hover:bg-slate-50"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px]">
            <main className="space-y-5 p-4 sm:p-6">
              <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <div className="flex items-start gap-3">
                  <span className="mt-3 h-4 w-4 rounded-full border-2 border-slate-400" />

                  <div className="min-w-0 flex-1">
                    <input
                      name="nom_asso"
                      value={form.nom_asso || ""}
                      onChange={(event) =>
                        updateField("nom_asso", event.target.value)
                      }
                      placeholder="Nom de l'association"
                      className="w-full rounded-lg border border-transparent bg-transparent px-2 py-1 text-2xl font-bold text-[#172b4d] outline-none hover:border-slate-200 focus:border-violet-300 focus:bg-white focus:ring-4 focus:ring-violet-100"
                    />

                    <div className="mt-2 text-sm text-slate-500">
                      Dans la liste{" "}
                      <span className="font-bold">
                        {currentColumn?.title || form.statut_asso || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-[#172b4d]">
                    Description
                  </h3>

                  {loading && (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-500">
                      Chargement...
                    </span>
                  )}
                </div>

                <RichTextEditor
                  value={form.detail_asso || ""}
                  onChange={(html) => updateField("detail_asso", html)}
                  placeholder="Ajoute les notes de relance, visio, contact, devis, prochaine étape..."
                />
              </section>

              <section className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                <h3 className="mb-4 text-lg font-bold text-[#172b4d]">
                  Informations
                </h3>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Produit">
                    <select
                      value={form.produit_asso || ""}
                      onChange={(event) =>
                        updateField("produit_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    >
                      <option value="">Produit non défini</option>

                      {productChoices.map((product) => (
                        <option key={product} value={product}>
                          {product}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Discipline">
                    <input
                      value={form.discipline_asso || ""}
                      onChange={(event) =>
                        updateField("discipline_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>

                  <Field label="Date de relance / fin abonnement">
                    <input
                      type="date"
                      value={form.date_relance_asso || ""}
                      onChange={(event) =>
                        updateField("date_relance_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>

                  <Field label="Dernier contact">
                    <input
                      type="date"
                      value={form.last_contact_asso || ""}
                      onChange={(event) =>
                        updateField("last_contact_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>

                  <Field label="Téléphone">
                    <input
                      value={form.tel_asso || ""}
                      onChange={(event) =>
                        updateField("tel_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>

                  <Field label="Email">
                    <input
                      type="email"
                      value={form.mail_asso || ""}
                      onChange={(event) =>
                        updateField("mail_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>

                  <Field label="Site web">
                    <input
                      value={form.siteweb_asso || ""}
                      onChange={(event) =>
                        updateField("siteweb_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>

                  <Field label="API URL">
                    <input
                      value={form.apiUrl_asso || ""}
                      onChange={(event) =>
                        updateField("apiUrl_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>

                  <Field label="SIRET">
                    <input
                      value={form.siret_asso || ""}
                      onChange={(event) =>
                        updateField("siret_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>

                  <Field label="Logo / image">
                    <input
                      value={form.logo_asso || ""}
                      onChange={(event) =>
                        updateField("logo_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>

                  <Field label="Adresse" className="sm:col-span-2">
                    <input
                      value={form.adresse_asso || ""}
                      onChange={(event) =>
                        updateField("adresse_asso", event.target.value)
                      }
                      className="input-suivi-client"
                    />
                  </Field>
                </div>
              </section>
            </main>

            <aside className="space-y-4 border-t border-slate-200 bg-white p-4 lg:border-l lg:border-t-0">
              <SideBlock title="Actions">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSave />
                  {saving ? "Sauvegarde..." : "Enregistrer"}
                </button>

                <button
                  type="button"
                  onClick={() => onAskDelete?.(association)}
                  disabled={saving}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaTrash />
                  Supprimer
                </button>
              </SideBlock>

              <SideBlock title="Résumé">
                <Info icon={FaCalendarAlt} label="Relance">
                  {form.date_relance_asso
                    ? formatDateFR(form.date_relance_asso)
                    : "Non définie"}
                </Info>

                <Info icon={FaPhoneAlt} label="Téléphone">
                  {form.tel_asso || "Non renseigné"}
                </Info>

                <Info icon={FaAt} label="Email">
                  {form.mail_asso || "Non renseigné"}
                </Info>

                <Info icon={FaGlobe} label="Site web">
                  {form.siteweb_asso ? (
                    <a
                      href={form.siteweb_asso}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 underline"
                    >
                      Ouvrir <FaExternalLinkAlt className="text-xs" />
                    </a>
                  ) : (
                    "Non renseigné"
                  )}
                </Info>

                <Info icon={FaMapMarkerAlt} label="Adresse">
                  {form.adresse_asso || "Non renseignée"}
                </Info>
              </SideBlock>

              <SideBlock title="Contacts liés">
                {contacts.length ? (
                  <div className="space-y-2">
                    {contacts.map((prospect) => (
                      <div
                        key={prospect.id}
                        className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                      >
                        <div className="flex items-center gap-2 font-bold text-slate-800">
                          <FaUserTie className="text-slate-400" />
                          {prospect.prenom} {prospect.nom}
                        </div>

                        {prospect.fonction && (
                          <div className="mt-1 text-xs text-slate-500">
                            {prospect.fonction}
                          </div>
                        )}

                        {prospect.telephone && (
                          <div className="mt-1 text-xs text-slate-600">
                            {prospect.telephone}
                          </div>
                        )}

                        {prospect.email && (
                          <div className="mt-1 text-xs text-blue-600">
                            {prospect.email}
                          </div>
                        )}

                        {prospect.notes && (
                          <div className="mt-2 rounded-lg bg-white p-2 text-xs leading-relaxed text-slate-500">
                            {prospect.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">
                    Aucun contact lié.
                  </div>
                )}
              </SideBlock>
            </aside>
          </div>
        </form>
      </div>

      <style>{`
        .input-suivi-client {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0.625rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
        }

        .input-suivi-client:focus {
          border-color: rgb(139 92 246);
          box-shadow: 0 0 0 4px rgb(237 233 254);
        }
      `}</style>
    </div>,
    document.body,
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

function SideBlock({ title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-bold text-[#172b4d]">{title}</h3>
      {children}
    </section>
  );
}

function Info({ icon: Icon, label, children }) {
  return (
    <div className="mb-3 flex items-start gap-3 rounded-xl bg-slate-50 p-3">
      <Icon className="mt-0.5 shrink-0 text-slate-400" />
      <div className="min-w-0">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
          {label}
        </div>
        <div className="break-words text-sm text-slate-700">{children}</div>
      </div>
    </div>
  );
}

function RichTextEditor({ value = "", onChange, placeholder }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (!editorRef.current) return;

    if (document.activeElement !== editorRef.current) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  const emitChange = () => {
    onChange?.(editorRef.current?.innerHTML || "");
  };

  const command = (name, commandValue = null) => {
    editorRef.current?.focus();
    document.execCommand(name, false, commandValue);
    emitChange();
  };

  const addLink = () => {
    const url = window.prompt("URL du lien :");

    if (!url) return;

    editorRef.current?.focus();
    document.execCommand("createLink", false, url);
    emitChange();
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 p-2">
        <ToolbarButton label="Gras" onClick={() => command("bold")}>
          <FaBold />
        </ToolbarButton>

        <ToolbarButton label="Italique" onClick={() => command("italic")}>
          <FaItalic />
        </ToolbarButton>

        <ToolbarButton label="Souligné" onClick={() => command("underline")}>
          <FaUnderline />
        </ToolbarButton>

        <Divider />

        <ToolbarButton
          label="Liste"
          onClick={() => command("insertUnorderedList")}
        >
          <FaListUl />
        </ToolbarButton>

        <ToolbarButton
          label="Liste numérotée"
          onClick={() => command("insertOrderedList")}
        >
          <FaListOl />
        </ToolbarButton>

        <Divider />

        <ToolbarButton label="Lien" onClick={addLink}>
          <FaLink />
        </ToolbarButton>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        data-placeholder={placeholder}
        className="rich-suivi-editor min-h-[230px] max-h-[430px] overflow-y-auto px-4 py-3 text-sm leading-relaxed text-slate-800 outline-none"
      />

      <style>{`
        .rich-suivi-editor:empty:before {
          content: attr(data-placeholder);
          color: rgb(148 163 184);
          pointer-events: none;
        }

        .rich-suivi-editor ul {
          list-style: disc;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }

        .rich-suivi-editor ol {
          list-style: decimal;
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }

        .rich-suivi-editor a {
          color: rgb(37 99 235);
          text-decoration: underline;
        }

        .rich-suivi-editor p {
          margin: 0.5rem 0;
        }
      `}</style>
    </div>
  );
}

function ToolbarButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      title={label}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 transition hover:bg-white hover:text-violet-700 hover:shadow-sm"
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-6 w-px bg-slate-200" />;
}
